import { isRequestError } from 'utils/requestError';
import { Card, InputPinCode, Loading, PopupWraper } from 'components';
import { Button, InputWraper, useForm } from 'modules';
import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode.react';
import toast from 'react-hot-toast';
import {
  useCheckPinCodeMutation,
  useDisableTwoFaMutation,
  useEnableTwoFaMutation,
  useGetTwoFaStatusQuery,
  useSetupTwoFaMutation,
} from 'services';
import type { Setup2faDataEntity } from 'services/openapi';
import * as Yup from 'yup';

type TwoFaMode =
  | 'idle'
  | 'enable-pin'
  | 'enable-setup'
  | 'disable-pin'
  | 'disable-otp';

const panelStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 760,
};

const cardStyle: React.CSSProperties = {
  padding: 24,
  minHeight: 160,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 12,
  flexWrap: 'wrap',
  marginBottom: 20,
};

const summaryStyle: React.CSSProperties = {
  display: 'grid',
  gap: 16,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: 24,
  fontWeight: 700,
};

const statusStyle: React.CSSProperties = {
  borderRadius: 999,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
};

const enabledStatusStyle: React.CSSProperties = {
  ...statusStyle,
  background: 'rgba(34, 197, 94, 0.16)',
  color: '#22c55e',
};

const disabledStatusStyle: React.CSSProperties = {
  ...statusStyle,
  background: 'rgba(239, 68, 68, 0.14)',
  color: '#ef4444',
};

const contentStyle: React.CSSProperties = {
  maxWidth: 620,
};

const actionStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  marginTop: 20,
};

const mainActionStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-start',
};

const alertStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  borderRadius: 8,
  padding: '12px 14px',
  marginTop: 16,
  fontSize: 13,
  fontWeight: 700,
};

const warningAlertStyle: React.CSSProperties = {
  ...alertStyle,
  background: 'rgba(245, 158, 11, 0.16)',
  color: '#fbbf24',
};

const successAlertStyle: React.CSSProperties = {
  ...alertStyle,
  background: 'rgba(34, 197, 94, 0.12)',
  color: '#22c55e',
};

const qrWrapStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  width: 'fit-content',
  background: '#fff',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 16,
  margin: '16px auto',
};

const setupTimeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 999,
  padding: '3px 9px',
  background: 'rgba(245, 158, 11, 0.18)',
  color: '#fbbf24',
  fontWeight: 800,
  whiteSpace: 'nowrap',
};

const popupContentStyle: React.CSSProperties = {
  padding: '8px 12px 4px',
  maxWidth: 560,
};

const secretStyle: React.CSSProperties = {
  wordBreak: 'break-all',
  color: 'var(--text-primary)',
  background: 'var(--bg-2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
  fontFamily: 'monospace',
};

const fieldTitleStyle: React.CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: 'uppercase',
  marginBottom: 8,
};

const noteStyle: React.CSSProperties = {
  color: 'var(--text-muted)',
  textAlign: 'center',
  fontSize: 14,
  lineHeight: 1.5,
  marginBottom: 12,
};

const pinValidation = Yup.string()
  .required('Vui lòng nhập mật khẩu')
  .length(6, 'Mật khẩu phải gồm 6 số');

const otpValidation = Yup.string()
  .required('Vui lòng nhập mã 2FA')
  .length(6, 'Mã 2FA phải gồm 6 số');

const useCountdown = (initialSeconds?: number) => {
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    setRemainingSeconds(initialSeconds || 0);
  }, [initialSeconds]);

  useEffect(() => {
    if (remainingSeconds <= 0) return;

    const timeout = window.setTimeout(() => {
      setRemainingSeconds((value) => Math.max(value - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [remainingSeconds]);

  return remainingSeconds;
};

const PersonalTwoFaSecurityFeature = () => {
  const [mode, setMode] = useState<TwoFaMode>('idle');
  const [setupData, setSetupData] = useState<Setup2faDataEntity>();
  const [verifiedPinCode, setVerifiedPinCode] = useState('');

  const {
    data: statusData,
    isLoading: isLoadingStatus,
    isFetching: isFetchingStatus,
    refetch: refetchStatus,
  } = useGetTwoFaStatusQuery();
  const { mutateAsync: checkPinCode, isLoading: isCheckingPinCode } =
    useCheckPinCodeMutation();
  const { mutateAsync: setupTwoFa, isLoading: isSettingUp } =
    useSetupTwoFaMutation();
  const { mutateAsync: enableTwoFa, isLoading: isEnabling } =
    useEnableTwoFaMutation();
  const { mutateAsync: disableTwoFa, isLoading: isDisabling } =
    useDisableTwoFaMutation();

  const mfaEnabled = !!statusData?.data?.mfaEnabled;
  const setupRemainingSeconds = useCountdown(setupData?.expiresIn);
  const isBusy =
    isLoadingStatus ||
    isFetchingStatus ||
    isCheckingPinCode ||
    isSettingUp ||
    isEnabling ||
    isDisabling;

  const clearFlow = () => {
    setMode('idle');
    setSetupData(undefined);
    setVerifiedPinCode('');
  };

  const {
    handleSubmit: handleSubmitEnablePin,
    getInputProps: getEnablePinProps,
    resetForm: resetEnablePinForm,
  } = useForm({
    structure: [
      {
        name: 'pinCode',
        validate: pinValidation,
      },
    ],
    onSubmit: async (values) => {
      try {
        const checkResult = await checkPinCode({ pinCode: values.pinCode });
        if (!checkResult?.isValid) {
          checkResult?.message?.map((item: string) => toast.error(item));
          return;
        }

        const response = await setupTwoFa({ pinCode: values.pinCode });
        setVerifiedPinCode(values.pinCode);
        setSetupData(response.data);
        setMode('enable-setup');
        resetEnablePinForm();
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error?.message || 'Không thể khởi tạo 2FA');
        }
      }
    },
  });

  const {
    handleSubmit: handleSubmitEnableOtp,
    getInputProps: getEnableOtpProps,
    resetForm: resetEnableOtpForm,
  } = useForm({
    structure: [
      {
        name: 'otpCode',
        validate: otpValidation,
      },
    ],
    onSubmit: async (values) => {
      if (!verifiedPinCode || !setupData || setupRemainingSeconds <= 0) {
        toast.error('Phiên thiết lập 2FA đã hết hạn, vui lòng tạo lại mã');
        return;
      }

      try {
        await enableTwoFa({
          pinCode: verifiedPinCode,
          otpCode: values.otpCode,
        });
        toast.success('Bật 2FA thành công');
        resetEnableOtpForm();
        clearFlow();
        refetchStatus();
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error?.message || 'Bật 2FA thất bại');
        }
      }
    },
  });

  const {
    handleSubmit: handleSubmitDisablePin,
    getInputProps: getDisablePinProps,
    resetForm: resetDisablePinForm,
  } = useForm({
    structure: [
      {
        name: 'pinCode',
        validate: pinValidation,
      },
    ],
    onSubmit: async (values) => {
      try {
        const checkResult = await checkPinCode({ pinCode: values.pinCode });
        if (!checkResult?.isValid) {
          checkResult?.message?.map((item: string) => toast.error(item));
          return;
        }

        setVerifiedPinCode(values.pinCode);
        setMode('disable-otp');
        resetDisablePinForm();
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error?.message || 'Không thể xác thực mật khẩu');
        }
      }
    },
  });

  const {
    handleSubmit: handleSubmitDisableOtp,
    getInputProps: getDisableOtpProps,
    resetForm: resetDisableOtpForm,
  } = useForm({
    structure: [
      {
        name: 'otpCode',
        validate: otpValidation,
      },
    ],
    onSubmit: async (values) => {
      if (!verifiedPinCode) {
        toast.error('Vui lòng xác thực mật khẩu trước');
        return;
      }

      try {
        await disableTwoFa({
          pinCode: verifiedPinCode,
          otpCode: values.otpCode,
        });
        toast.success('Tắt 2FA thành công');
        resetDisableOtpForm();
        clearFlow();
        refetchStatus();
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error?.message || 'Tắt 2FA thất bại');
        }
      }
    },
  });

  const statusLabel = useMemo(
    () => (mfaEnabled ? 'Đang bật' : 'Đang tắt'),
    [mfaEnabled],
  );

  return (
    <div style={panelStyle}>
      <Card>
        <div style={cardStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>Bảo mật 2FA</h2>
            <div style={mfaEnabled ? enabledStatusStyle : disabledStatusStyle}>
              {statusLabel}
            </div>
          </div>

          <Loading isLoading={isLoadingStatus}>
            <div style={summaryStyle}>
              <div style={contentStyle}>
                <p style={noteStyle}>
                  {mfaEnabled
                    ? 'Tài khoản đang được bảo vệ bằng mã xác thực từ ứng dụng Authenticator.'
                    : 'Bật 2FA để bảo vệ tài khoản bằng mã xác thực từ ứng dụng Authenticator, ngoài mật khẩu đăng nhập.'}
                </p>
                <div style={mfaEnabled ? successAlertStyle : warningAlertStyle}>
                  {mfaEnabled
                    ? 'Tài khoản đang được bảo vệ bằng lớp xác thực thứ hai.'
                    : 'Tài khoản chưa được bảo vệ bằng lớp xác thực thứ hai.'}
                </div>
              </div>
              <div style={mainActionStyle}>
                {mfaEnabled ? (
                  <Button
                    label='Tắt 2FA'
                    buttonType='danger-outline'
                    onClick={() => setMode('disable-pin')}
                    disabled={isBusy}
                  />
                ) : (
                  <Button
                    label='Bật 2FA'
                    buttonType='success'
                    onClick={() => setMode('enable-pin')}
                    disabled={isBusy}
                  />
                )}
              </div>
            </div>
          </Loading>
        </div>

        {mode === 'enable-pin' && (
          <PopupWraper
            center
            title='Xác thực mật khẩu'
            onClose={() => {
              resetEnablePinForm();
              clearFlow();
            }}
            style={{ maxWidth: 520 }}
          >
            <form onSubmit={handleSubmitEnablePin} style={popupContentStyle}>
              <p style={noteStyle}>
                Nhập mật khẩu hiện tại để bắt đầu thiết lập 2FA.
              </p>
              <InputWraper
                label=''
                inputProps={{
                  ...getEnablePinProps('pinCode'),
                  autoFocus: true,
                }}
                component={InputPinCode}
              />
              <div style={actionStyle}>
                <Button
                  label='Hủy'
                  buttonType='grey'
                  onClick={() => {
                    resetEnablePinForm();
                    clearFlow();
                  }}
                />
                <Button
                  label='Tiếp tục'
                  buttonType='success'
                  type='submit'
                  disabled={isBusy}
                  isLoading={isBusy}
                />
              </div>
            </form>
          </PopupWraper>
        )}

        {mode === 'enable-setup' && setupData && (
          <PopupWraper
            center
            title='Thiết lập 2FA'
            onClose={() => {
              resetEnableOtpForm();
              clearFlow();
            }}
            style={{ maxWidth: 620 }}
          >
            <form onSubmit={handleSubmitEnableOtp} style={popupContentStyle}>
              <p style={noteStyle}>
                Quét QR bằng ứng dụng Authenticator hoặc nhập "KHÓA THIẾT LẬP",
                sau đó nhập mã 6 số để hoàn tất.
              </p>
              <p style={noteStyle}>
                Mã thiết lập hết hạn sau:{' '}
                <span style={setupTimeStyle}>{setupRemainingSeconds}s</span>
              </p>
              <div style={qrWrapStyle}>
                <QRCode value={setupData.provisioningUri} size={220} />
              </div>
              <div style={fieldTitleStyle}>Khóa thiết lập</div>
              <div style={secretStyle}>{setupData.secret}</div>
              <InputWraper
                label='Mã 2FA'
                inputProps={{
                  ...getEnableOtpProps('otpCode'),
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                component={InputPinCode}
              />
              <div style={actionStyle}>
                <Button
                  label='Hủy'
                  buttonType='grey'
                  onClick={() => {
                    resetEnableOtpForm();
                    clearFlow();
                  }}
                />
                <Button
                  label='Tạo lại mã'
                  buttonType='primary-outline'
                  onClick={() => {
                    resetEnableOtpForm();
                    setSetupData(undefined);
                    setVerifiedPinCode('');
                    setMode('enable-pin');
                  }}
                />
                <Button
                  label='Xác nhận bật 2FA'
                  buttonType='success'
                  type='submit'
                  disabled={isBusy || setupRemainingSeconds <= 0}
                  isLoading={isEnabling}
                />
              </div>
            </form>
          </PopupWraper>
        )}

        {mode === 'disable-pin' && (
          <PopupWraper
            center
            title='Xác thực mật khẩu'
            onClose={() => {
              resetDisablePinForm();
              clearFlow();
            }}
            style={{ maxWidth: 520 }}
          >
            <form onSubmit={handleSubmitDisablePin} style={popupContentStyle}>
              <p style={noteStyle}>Nhập mật khẩu hiện tại trước khi tắt 2FA.</p>
              <InputWraper
                label='Mật khẩu'
                inputProps={{
                  ...getDisablePinProps('pinCode'),
                  autoFocus: true,
                }}
                component={InputPinCode}
              />
              <div style={actionStyle}>
                <Button
                  label='Hủy'
                  buttonType='grey'
                  onClick={() => {
                    resetDisablePinForm();
                    clearFlow();
                  }}
                />
                <Button
                  label='Tiếp tục'
                  buttonType='danger'
                  type='submit'
                  disabled={isBusy}
                  isLoading={isBusy}
                />
              </div>
            </form>
          </PopupWraper>
        )}

        {mode === 'disable-otp' && (
          <PopupWraper
            center
            title='Xác nhận tắt 2FA'
            onClose={() => {
              resetDisableOtpForm();
              clearFlow();
            }}
            style={{ maxWidth: 520 }}
          >
            <form onSubmit={handleSubmitDisableOtp} style={popupContentStyle}>
              <p style={noteStyle}>
                Nhập mã 2FA hiện tại từ ứng dụng Authenticator để xác nhận tắt
                bảo mật 2FA.
              </p>
              <InputWraper
                label='Mã 2FA'
                inputProps={{
                  ...getDisableOtpProps('otpCode'),
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                component={InputPinCode}
              />
              <div style={actionStyle}>
                <Button
                  label='Hủy'
                  buttonType='grey'
                  onClick={() => {
                    resetDisableOtpForm();
                    clearFlow();
                  }}
                />
                <Button
                  label='Xác nhận tắt 2FA'
                  buttonType='danger'
                  type='submit'
                  disabled={isBusy}
                  isLoading={isDisabling}
                />
              </div>
            </form>
          </PopupWraper>
        )}
      </Card>
    </div>
  );
};

export default PersonalTwoFaSecurityFeature;
